import { useCallback, useRef } from 'react'

/**
 * SCRUM-1032 — one save at a time, per grid.
 *
 * Every bulk-edit grid stages its rows and clears them only once each write has
 * come back. In the guided setup two things can start a save: the pause
 * autosave, and the wizard flushing every grid when the user presses Next. If
 * the second starts while the first is still writing, both read the same staged
 * batch and POST it twice — which is how a pipe, and a station, ended up in the
 * list in duplicate (QC items 12 and 15b).
 *
 * Runs are chained rather than dropped: the second starts from what the first
 * left behind, where a row that has landed is no longer staged and the save's
 * own "nothing to write" early return takes it. Dropping instead would lose the
 * flush the wizard is relying on before it changes step.
 *
 * `save` is stable, so passing it to `useGuidedAutosave` does not restart that
 * hook's timer on every render — the timer is driven by its `version`.
 */
/**
 * The same gate, shared by more than one function.
 *
 * The station grid writes through two entry points — the pause autosave and the
 * bulk save the wizard calls on Next — and it is the pair of them overlapping
 * that duplicates rows, so a gate around either one alone fixes nothing. Pass
 * each through `run` and they queue behind one another.
 */
export const useSerialisedRunner = () => {
  const inFlightRef = useRef<Promise<unknown> | null>(null)

  const run = useCallback(<T,>(task: () => Promise<T>): Promise<T> => {
    const chained: Promise<T> = (inFlightRef.current ?? Promise.resolve())
      .catch(() => undefined)
      .then(() => task())
      .finally(() => {
        if (inFlightRef.current === chained) inFlightRef.current = null
      })

    inFlightRef.current = chained
    return chained
  }, [])

  const isRunning = useCallback(() => inFlightRef.current !== null, [])

  return { run, isRunning }
}

const useSerialisedSave = <T, A extends unknown[] = []>(
  performSave: (...args: A) => Promise<T>,
) => {
  // Untyped: the slot only sequences runs, it does not carry their result.
  const inFlightRef = useRef<Promise<unknown> | null>(null)

  // Read through a ref so `save` can stay stable while still calling the
  // latest closure, which is the one holding the current staged rows.
  const performRef = useRef(performSave)
  performRef.current = performSave

  // Arguments are passed through to this run (the guided autosave's
  // `leaveEditorOpen`).
  const save = useCallback((...args: A): Promise<T> => {
    // `catch` before the chain: one failed run must not stop the next from
    // being attempted. The caller still sees its own run's result — the
    // toolbar's Save reads the failure count back off it.
    const run: Promise<T> = (inFlightRef.current ?? Promise.resolve())
      .catch(() => undefined)
      .then(() => performRef.current(...args))
      .finally(() => {
        // Only clear the slot if this is still the newest run — a run that
        // finished while a later one was queued must not release the gate.
        if (inFlightRef.current === run) inFlightRef.current = null
      })

    inFlightRef.current = run
    return run
  }, [])

  /** True while a run is in flight, for the autosave timer to hold off. */
  const isSaving = useCallback(() => inFlightRef.current !== null, [])

  return { save, isSaving }
}

export default useSerialisedSave
